import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  connectQZTray, isQZConnected, getAvailablePrinters,
  getSavedPrinter, savePrinter, saveMethod, printReceipt
} from "@/utils/printService";
import {
  isWebSerialSupported, isSerialPrinterConnected,
  requestSerialPrinter, reconnectSerialPrinter, disconnectSerialPrinter,
  printViaSerial
} from "@/utils/webSerialPrintService";
import {
  Printer, Wifi, WifiOff, Volume2, VolumeX, CheckCircle2, RefreshCw,
  ChefHat, Wine, CreditCard, LayoutGrid, Plug, PlugZap, AlertCircle,
  Cable, Settings, BookOpen, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const POLL_INTERVAL = 5000;

const STATION_ICONS = {
  cozinha: ChefHat, cocina: ChefHat, kitchen: ChefHat,
  bar: Wine, bebidas: Wine, drinks: Wine,
  caixa: CreditCard, cashier: CreditCard, cajero: CreditCard,
};
function getStationIcon(alias) {
  return STATION_ICONS[(alias || "").toLowerCase().trim()] || Printer;
}

function filterItemsByTemplate(items, template) {
  if (!template || template === "complete" || template === "minimal") return items;
  const drinkPattern = /bebida|drink|cerveza|vino|wine|beer|agua|water|cafe|coffee|refresco|zumo|juice|copa|cocktail|gin|vodka|rum|whisky|mojito|sangria|soda|cola|fanta|sprite/i;
  if (template === "cocina" || template === "kitchen") {
    return items.filter(i => !drinkPattern.test(i.product_name || i.name || ""));
  }
  if (template === "bar") {
    return items.filter(i => drinkPattern.test(i.product_name || i.name || ""));
  }
  return items;
}

export default function PrintStationPage() {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [printedCount, setPrintedCount] = useState(0);
  const [lastCheck, setLastCheck] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState("all");
  const intervalRef = useRef(null);
  const [restaurantData, setRestaurantData] = useState({});

  // Print method state
  const [printMethod, setPrintMethod] = useState("connecting"); // "qz", "serial", "none", "connecting"
  const [qzConnected, setQzConnected] = useState(false);
  const [qzPrinters, setQzPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(() => getSavedPrinter());
  const [serialSupported] = useState(isWebSerialSupported());
  const [serialConnected, setSerialConnected] = useState(false);

  // Load stations and restaurant data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    saveMethod("qz"); // Default to QZ for Print Station
    authAxiosRef.current.get("/print/stations").then(r => {
      const data = r.data?.data || r.data;
      setStations(data?.stations || []);
      const params = new URLSearchParams(window.location.search);
      const urlStation = params.get("station");
      if (urlStation) setSelectedStation(urlStation.toLowerCase());
    }).catch(() => {});
    // Load restaurant data for receipt branding
    authAxiosRef.current.get("/restaurant").then(r => {
      setRestaurantData(r.data || {});
    }).catch(() => {});
  }, []);

  // Auto-connect to QZ Tray on mount
  useEffect(() => {
    let cancelled = false;
    async function tryConnect() {
      try {
        const ok = await connectQZTray({ manual: false });
        if (cancelled) return;
        if (ok) {
          setQzConnected(true);
          setPrintMethod("qz");
          const printers = await getAvailablePrinters();
          setQzPrinters(printers);
          const saved = getSavedPrinter();
          if (saved && printers.includes(saved)) {
            setSelectedPrinter(saved);
          } else if (printers.length > 0) {
            setSelectedPrinter(printers[0]);
            savePrinter(printers[0]);
          }
          toast.success(`QZ Tray conectado! ${printers.length} impressora(s) encontrada(s)`);
          return;
        }
      } catch {}

      // Fallback: try Web Serial
      if (serialSupported) {
        try {
          const result = await reconnectSerialPrinter();
          if (cancelled) return;
          if (result) {
            setSerialConnected(true);
            setPrintMethod("serial");
            saveMethod("serial");
            toast.info("QZ Tray nao encontrado. Usando impressora serial.");
            return;
          }
        } catch {}
      }

      if (!cancelled) setPrintMethod("none");
    }
    tryConnect();
    return () => { cancelled = true; };
  }, [serialSupported]);

  // Manual QZ connect
  const handleConnectQZ = async () => {
    setPrintMethod("connecting");
    try {
      const ok = await connectQZTray({ manual: true });
      if (ok) {
        setQzConnected(true);
        setPrintMethod("qz");
        saveMethod("qz");
        const printers = await getAvailablePrinters();
        setQzPrinters(printers);
        if (printers.length > 0 && !selectedPrinter) {
          setSelectedPrinter(printers[0]);
          savePrinter(printers[0]);
        }
        toast.success(`QZ Tray conectado! ${printers.length} impressora(s)`);
      } else {
        setPrintMethod("none");
        toast.error("QZ Tray nao respondeu. Verifique se esta rodando.");
      }
    } catch (e) {
      setPrintMethod("none");
      toast.error(e.message);
    }
  };

  // Manual Serial connect
  const handleConnectSerial = async () => {
    try {
      await requestSerialPrinter();
      setSerialConnected(true);
      setPrintMethod("serial");
      saveMethod("serial");
      toast.success("Impressora serial conectada!");
    } catch (e) {
      toast.error("Erro: " + e.message);
    }
  };

  const playAlert = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880; osc.type = "sine"; gain.gain.value = 0.3;
      osc.start();
      setTimeout(() => { osc.frequency.value = 1100; }, 150);
      setTimeout(() => { osc.frequency.value = 880; }, 300);
      setTimeout(() => { osc.stop(); ctx.close(); }, 500);
    } catch {}
  }, [soundEnabled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const printJobDirect = useCallback(async (job) => {
    setProcessing(true);
    const order = job?.order_data || {};
    const rest = job?.restaurant_data || restaurantData || {};
    const rc = rest.receipt_config || restaurantData.receipt_config || {};
    const template = job?.template || "complete";
    const config = job?.station_config || {};
    const items = filterItemsByTemplate(order.items || [], template);

    if (items.length === 0) {
      try { await authAxiosRef.current.post(`/print/complete/${job.id}`); } catch {}
      setProcessing(false);
      return;
    }

    const filteredOrder = { ...order, items };

    // Build full restaurant branding for receipt
    const fullRestData = {
      name: template === "complete" ? (rest.name || restaurantData.name || "") : (job.station || template).toUpperCase(),
      cif: rest.cif || restaurantData.cif || "",
      address: rc.show_address !== false ? (rc.address || rest.address || restaurantData.address || "") : "",
      phone: rc.show_phone !== false ? (rc.phone || rest.phone || restaurantData.phone || "") : "",
      currency: rest.currency || restaurantData.currency || "eur",
      slug: rest.slug || restaurantData.slug || "",
      receipt_config: {
        ...rc,
        receipt_footer: rc.receipt_footer || "Obrigado pela preferencia!",
        show_qr_code: template === "complete" ? (rc.show_qr_code !== false) : false,
        qr_message: rc.qr_message || "Escaneie e veja nosso menu",
        menu_url: rc.menu_url || "",
        instagram: rc.instagram || rest.instagram_url || restaurantData.instagram_url || "",
        whatsapp_phone: rc.whatsapp_phone || "",
      },
      instagram_url: rc.instagram || rest.instagram_url || restaurantData.instagram_url || "",
      receipt_footer: rc.receipt_footer || "Obrigado pela preferencia!",
    };

    try {
      if (printMethod === "qz" && isQZConnected()) {
        const printerConfig = {
          printer_name: selectedPrinter || getSavedPrinter(),
          columns: config.double_font_size ? 24 : 42,
          copies: job.copies || 1,
          show_store_name: true,
          show_order_number: true,
          show_cif: template === "complete",
        };
        await printReceipt(filteredOrder, printerConfig, fullRestData);
      } else if (printMethod === "serial" && isSerialPrinterConnected()) {
        const printOptions = {
          storeName: fullRestData.name,
          address: fullRestData.address,
          phone: fullRestData.phone,
          cif: template === "complete" ? fullRestData.cif : "",
          currency: fullRestData.currency,
          receiptFooter: fullRestData.receipt_footer,
          menuUrl: fullRestData.receipt_config.menu_url || (fullRestData.slug ? `https://kaisosystem.com/menu/${fullRestData.slug}` : ""),
          showQRCode: fullRestData.receipt_config.show_qr_code,
          qrMessage: fullRestData.receipt_config.qr_message,
          instagram: fullRestData.receipt_config.instagram,
          whatsappPhone: fullRestData.receipt_config.whatsapp_phone,
          copies: job.copies || 1,
          columns: config.double_font_size ? 24 : 32,
        };
        await printViaSerial(filteredOrder, printOptions);
      } else {
        toast.warning("Nenhuma impressora conectada. Conecte via QZ Tray ou Porta Serial.");
        setProcessing(false);
        return;
      }

      // Mark as completed
      try { await authAxiosRef.current.post(`/print/complete/${job.id}`); } catch {}
      setPrintedCount(prev => prev + 1);
      setRecentJobs(prev => [{ ...job, printed_at: new Date().toISOString() }, ...prev].slice(0, 20));
    } catch (e) {
      console.error("Print failed:", e);
      toast.error("Erro na impressao: " + e.message);
    }

    setProcessing(false);
  }, [printMethod, selectedPrinter, restaurantData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pollForJobs = useCallback(async () => {
    try {
      const stationParam = selectedStation !== "all" ? `?station=${selectedStation}` : "";
      const r = await authAxiosRef.current.get(`/print/pending${stationParam}`);
      const data = r.data?.data || r.data;
      const jobs = data?.jobs || [];
      setConnected(true);
      setLastCheck(new Date());
      if (jobs.length > 0 && !processing) {
        playAlert();
        await printJobDirect(jobs[0]);
      }
    } catch (e) {
      setConnected(false);
    }
  }, [processing, playAlert, printJobDirect, selectedStation]);

  useEffect(() => {
    pollForJobs();
    intervalRef.current = setInterval(pollForJobs, POLL_INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [pollForJobs]);

  // Wake lock
  useEffect(() => {
    let wakeLock = null;
    (async () => { try { if ("wakeLock" in navigator) wakeLock = await navigator.wakeLock.request("screen"); } catch {} })();
    return () => { if (wakeLock) wakeLock.release(); };
  }, []);

  const StationIcon = getStationIcon(selectedStation);
  const isPrinterReady = printMethod === "qz" || printMethod === "serial";

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col" data-testid="print-station-page">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StationIcon className="w-6 h-6 text-orange-500" />
          <div>
            <h1 className="font-bold text-lg" data-testid="station-title">
              Kaiso Print {selectedStation !== "all" ? `- ${selectedStation.charAt(0).toUpperCase() + selectedStation.slice(1)}` : "Station"}
            </h1>
            <p className="text-xs text-gray-400">{user?.restaurant_name || "Restaurante"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => window.open("/receipt-config", "_blank")} className="text-gray-400 hover:text-white" data-testid="open-receipt-config-btn" title="Configurar recibo">
            <FileText className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.open("/printer-guide", "_blank")} className="text-gray-400 hover:text-white" data-testid="open-guide-btn" title="Guia de configuracao">
            <BookOpen className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.open("/printer-setup", "_blank")} className="text-gray-400 hover:text-white" data-testid="open-settings-btn" title="Configuracoes">
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)} className="text-gray-400 hover:text-white" data-testid="toggle-sound-btn">
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${connected ? "bg-green-900/50 text-green-400" : "bg-red-900/50 text-red-400"}`} data-testid="connection-status">
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? "Online" : "Offline"}
          </div>
        </div>
      </div>

      {/* Printer connection bar */}
      <div className={`px-4 py-2 flex items-center justify-between flex-wrap gap-2 ${
        isPrinterReady ? "bg-green-900/30 border-b border-green-800" : printMethod === "connecting" ? "bg-blue-900/30 border-b border-blue-800" : "bg-amber-900/30 border-b border-amber-800"
      }`} data-testid="printer-status-bar">
        {printMethod === "qz" && (
          <div className="flex items-center gap-2">
            <PlugZap className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-300 font-medium">
              QZ Tray conectado - impressao automatica SEM popup
              {selectedPrinter && <span className="text-green-500 ml-1">({selectedPrinter})</span>}
            </span>
          </div>
        )}
        {printMethod === "serial" && (
          <div className="flex items-center gap-2">
            <Cable className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-300 font-medium">Impressora serial conectada - impressao direta</span>
          </div>
        )}
        {printMethod === "connecting" && (
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
            <span className="text-sm text-blue-300">Conectando ao QZ Tray...</span>
          </div>
        )}
        {printMethod === "none" && (
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-300">Impressora nao conectada</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {printMethod === "none" && (
            <>
              <Button size="sm" onClick={handleConnectQZ} className="bg-orange-600 hover:bg-orange-700 text-white gap-1 h-8" data-testid="connect-qz-btn">
                <Wifi className="w-3.5 h-3.5" /> QZ Tray
              </Button>
              {serialSupported && (
                <Button size="sm" variant="outline" onClick={handleConnectSerial} className="text-gray-300 border-gray-600 gap-1 h-8" data-testid="connect-serial-btn">
                  <Cable className="w-3.5 h-3.5" /> Serial
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => window.open("/printer-guide", "_blank")} className="text-amber-400 text-xs h-8" data-testid="setup-guide-link">
                <BookOpen className="w-3.5 h-3.5 mr-1" /> Guia de instalacao
              </Button>
            </>
          )}
          {printMethod === "qz" && qzPrinters.length > 1 && (
            <select
              value={selectedPrinter}
              onChange={e => { setSelectedPrinter(e.target.value); savePrinter(e.target.value); toast.success(`Impressora: ${e.target.value}`); }}
              className="bg-gray-800 text-white text-xs border border-gray-700 rounded px-2 py-1 h-8"
              data-testid="printer-selector"
            >
              {qzPrinters.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Station selector */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-4 py-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          <span className="text-xs text-gray-500 flex-shrink-0">Estacao:</span>
          <Button variant={selectedStation === "all" ? "default" : "ghost"} size="sm" className={`h-7 text-xs gap-1 ${selectedStation === "all" ? "bg-orange-600" : "text-gray-400"}`} onClick={() => setSelectedStation("all")} data-testid="station-all-btn">
            <LayoutGrid className="w-3 h-3" /> Todas
          </Button>
          {stations.map((st, i) => {
            const alias = (st.alias || `estacao-${i + 1}`).toLowerCase().trim();
            const Icon = getStationIcon(alias);
            return (
              <Button key={i} variant={selectedStation === alias ? "default" : "ghost"} size="sm" className={`h-7 text-xs gap-1 ${selectedStation === alias ? "bg-orange-600" : "text-gray-400"}`} onClick={() => setSelectedStation(alias)} data-testid={`station-${alias}-btn`}>
                <Icon className="w-3 h-3" /> {st.alias || `Estacao ${i + 1}`}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        <div className="text-center space-y-4">
          <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${
            processing ? "bg-orange-900/30 border-2 border-orange-500 animate-pulse"
            : isPrinterReady && connected ? "bg-green-900/30 border-2 border-green-700"
            : "bg-red-900/30 border-2 border-red-700"
          }`}>
            <StationIcon className={`w-12 h-12 ${
              processing ? "text-orange-500"
              : isPrinterReady && connected ? "text-green-500"
              : "text-red-500"
            }`} />
          </div>
          <h2 className="text-xl font-bold text-gray-200" data-testid="status-text">
            {processing ? "Imprimindo..." : isPrinterReady && connected ? "Aguardando pedidos..." : !isPrinterReady ? "Conecte a impressora" : "Reconectando..."}
          </h2>
          <p className="text-sm text-gray-500">
            {lastCheck && `${lastCheck.toLocaleTimeString()}`}
            {printMethod === "qz" && " | QZ Tray ativo"}
            {printMethod === "serial" && " | Serial ativa"}
            {printMethod === "none" && " | Clique em 'QZ Tray' ou 'Serial' acima"}
          </p>
          {printMethod === "none" && (
            <div className="bg-amber-900/20 border border-amber-800 rounded-lg p-4 max-w-md mx-auto text-left" data-testid="setup-instructions">
              <p className="text-sm text-amber-300 font-bold mb-2">Como configurar (igual Menu Integrado):</p>
              <ol className="text-xs text-amber-200/80 space-y-1 list-decimal list-inside">
                <li>Instale o <a href="https://www.java.com/download/" target="_blank" rel="noopener noreferrer" className="underline text-amber-300">Java</a></li>
                <li>Instale o <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="underline text-amber-300">QZ Tray</a></li>
                <li>Baixe e execute o <button onClick={() => window.open("/printer-guide", "_blank")} className="underline text-amber-300">instalador do certificado</button></li>
                <li>Reinicie o computador</li>
                <li>Abra esta pagina novamente</li>
              </ol>
              <Button size="sm" onClick={() => window.open("/printer-guide", "_blank")} className="mt-3 bg-amber-600 hover:bg-amber-700 text-white gap-1 w-full" data-testid="full-guide-btn">
                <BookOpen className="w-3.5 h-3.5" /> Ver guia completo
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-3 text-center">
              <div className="text-3xl font-bold text-orange-400" data-testid="printed-count">{printedCount}</div>
              <div className="text-xs text-gray-500">Impressos</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-3 text-center">
              <div className={`text-3xl font-bold ${isPrinterReady ? "text-green-400" : "text-amber-400"}`} data-testid="printer-status-indicator">
                {isPrinterReady ? "OK" : "---"}
              </div>
              <div className="text-xs text-gray-500">
                {printMethod === "qz" ? "QZ Tray" : printMethod === "serial" ? "Serial" : "Impressora"}
              </div>
            </CardContent>
          </Card>
        </div>

        <Button variant="outline" onClick={() => { pollForJobs(); toast.info("Verificando..."); }} className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="manual-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Verificar agora
        </Button>

        {/* Recent prints */}
        {recentJobs.length > 0 && (
          <div className="w-full max-w-md" data-testid="recent-jobs">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Impressos:</h3>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {recentJobs.map((job, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-900 rounded px-3 py-1.5 text-xs">
                  <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                  <span className="text-gray-300 font-mono">#{(job.order_data?.order_number || job.order_id || "").toString().slice(0, 8).toUpperCase()}</span>
                  <span className="text-orange-400 text-[10px] px-1 py-0.5 bg-orange-900/30 rounded">{job.station || "all"}</span>
                  <span className="text-gray-500 flex-1 truncate">{job.order_data?.customer_name || ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-2 text-center text-xs text-gray-600">
        Kaiso Print v3.0 | {printMethod === "qz" ? "QZ Tray - Impressao SILENCIOSA" : printMethod === "serial" ? "Serial - Impressao direta" : "Conecte uma impressora para imprimir automaticamente"}
      </div>
    </div>
  );
}
